import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { generateRandomString } from "utils/helper";
import * as service from "../service/MyTimesheetService";
import { setIsLoading, setMyTimesheetList, } from "../slices/myTimesheetSlice";
import dayjs from "dayjs";
import { message, Modal } from "antd";
import useMyTimesheet from "./useMyTimesheet";

const useUpdateMyTimesheet = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [activePanel, setActivePanel] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const { currentCompany, currentEmployee } = useSelector((state) => state.company);
    const [pagination, setPagination] = useState({ page: 0, size: 10 });
    const { isLoading, myTimesheetList } = useSelector((state) => state.myTimesheet);
    const [editRecord, setEditRecord] = useState(null);
    const [timesheetOptions, setTimesheetOptions] = useState([]);
    const [roleOptions, setRoleOptions] = useState([]);
    const [taskOptions, setTaskOptions] = useState([]);
    const [selectedTimeSheetId, setSelectedTimeSheetId] = useState(null);
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [search, setSearch] = useState({});

    const { getTimesheetCalendarData, startDate, endDate } = useMyTimesheet();

    const toParams = useCallback((params) => {
        const { pagination = {}, sortBy = [] } = params;
        const modifiedParams = {
            ...params,
            page: pagination.page || 0,
            size: pagination.pageSize || 10,
            sortBy: sortBy
                .filter((s) => s.order !== undefined)
                .map((s) => ({
                    direction: s.order === "descend" ? "desc" : "asc",
                    property: s.field,
                })),
        };
        return modifiedParams;

    }, [])

    const onSelectChange = newSelectedRowKeys => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const modifiedTimesheetOption = useMemo(() => {
        const modifiedOptions = timesheetOptions?.map((opt) => ({
            ...opt,
            value: opt?.stpTimeSheetId,
            label: opt?.projectName,
        }));

        return modifiedOptions;
    }, [timesheetOptions]);

    const modifiedRoleOption = useMemo(() => {
        const modifiedOptions = roleOptions?.map((opt) => ({
            ...opt,
            value: opt?.stpTimeSheetRoleId,
            label: opt?.roleName,
        }));

        return modifiedOptions;
    }, [roleOptions]);

    const buildTreeFromRawData = (data, lang = "th") => {
        if (!Array.isArray(data)) return [];

        const flatList = [];

        data.forEach(task => {
            const hasChildren = Array.isArray(task.children) && task.children.length > 0;

            flatList.push({
                id: task.id,
                name: task.taskName,
                nameEn: task.taskNameEn,
                label: lang === "en" ? task.taskNameEn : task.taskName,
                value: task.id,
                disabled: hasChildren,
            });

            if (hasChildren) {
                task.children.forEach(subTask => {
                    flatList.push({
                        id: subTask.id,
                        name: subTask.taskName,
                        nameEn: subTask.taskNameEn,
                        label: lang === "en" ? subTask.taskNameEn : subTask.taskName,
                        value: subTask.id,
                        disabled: false,
                        under: { id: task.id },
                    });
                });
            }
        });

        return flatList;
    };


    console.log("task option log", taskOptions);

    const modifiedTaskOption = useMemo(() => {
        return buildTreeFromRawData(taskOptions, i18n.language);
    }, [taskOptions, i18n.language]);

    console.log("modifiedTaskOptionmodifiedTaskOption", buildTreeFromRawData(taskOptions));

    const getTimesheetByEmpIdOptions = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    employeeId: currentEmployee
                };
                const response = await service.dropdownProjectByEmpId(values);
                setTimesheetOptions(response?.data?.content);
            } catch (error) {
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, currentEmployee, dispatch]);

    const getRoleByEmpIdOptions = useCallback(async (timeSheetId) => {
        if (typeof currentCompany !== "object" && timeSheetId) {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    employeeId: currentEmployee,
                    stpTimeSheetId: timeSheetId
                };
                const response = await service.dropdownRoleByEmpIdAndStpTimeSheetId(values);
                setRoleOptions(response?.data?.content);
            } catch (error) {
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, currentEmployee, dispatch]);

    const getTaskByEmpIdOptions = useCallback(async (timeSheetId, roleId) => {
        if (typeof currentCompany !== "object" && timeSheetId) {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    employeeId: currentEmployee,
                    stpTimeSheetId: timeSheetId,
                    roleId: roleId
                };
                const response = await service.dropdownTaskByRole(values);
                setTaskOptions(response?.data?.content);
            } catch (error) {
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, currentEmployee, dispatch]);

    const getMyTimesheetList = useCallback(async (params) => {
        try {
            dispatch(setIsLoading(true))
            const response = await service.getMyTimesheetList({
                ...params,
                companyId: currentCompany,
                employeeId: currentEmployee,
            })

            dispatch(setMyTimesheetList(response?.data?.page?.content || []))
            setPagination({
                ...params?.pagination,
                current: response?.data?.page?.number + 1,
                pageNo: response?.data?.page?.number + 1,
                page: response?.data?.page?.number + 1,
                total: response.data?.page?.totalElements,
                size: response?.data?.page?.totalPages
            });
            dispatch(setIsLoading(false))
        } catch (error) {
            console.log("error ", error);
            dispatch(setIsLoading(false))
        }
    }, [currentCompany, currentEmployee])

    const onUpdateTimesheet = useCallback(async (params) => {
        dispatch(setIsLoading(true))
        const time = dayjs(params?.totalWorkingHours);
        const hours = time?.hour();
        const minutes = time?.minute();

        await service.updateMyTimesheet({
            ...params,
            companyId: currentCompany,
            employeeId: currentEmployee,
            startDate: dayjs(params?.recordingDate)?.format("YYYY-MM-DD"),
            totalWorkingHours: hours,
            totalWorkingMinutes: minutes,
            billableFlag: params?.billableFlag ? "Y" : "N",
            reworkFlag: params?.reworkFlag ? "Y" : "N",

        }).then((res) => {
            getMyTimesheetList({
                page: pagination?.page - 1,
                size: pagination?.pageSize || 10,
                pageSize: pagination?.pageSize || 10,
                current: pagination.current - 1,
            })
            getTimesheetCalendarData(startDate, endDate);
        }).catch((error) => {
            message.error(t("common.error"))
        }).finally((o) => {
            message.success(t("common.success"))
            dispatch(setIsLoading(false));
        })
    }, [currentCompany, currentEmployee, dispatch, getMyTimesheetList, pagination])

    const onPageChange = useCallback(
        (tablePagination, tableFilters, tableSorter, tableExtra) => {

            const modifiedSorter = Array.isArray(tableSorter)
                ? [...tableSorter]
                : [tableSorter];

            const modifiedParams = toParams({
                pagination: {
                    page: tablePagination?.current - 1,
                    size: tablePagination?.pageSize,
                    pageSize: tablePagination?.pageSize,
                    current: tablePagination.current - 1,
                },
                sortBy: modifiedSorter,
            });
            getMyTimesheetList(modifiedParams);
        },
        [getMyTimesheetList]
    );

    useEffect(() => {
        if (currentCompany) {
            getMyTimesheetList({ page: 0, size: 10 });
            getTimesheetByEmpIdOptions()
        }
    }, [currentCompany])

    useEffect(() => {
        if (selectedTimeSheetId) {
            getRoleByEmpIdOptions(selectedTimeSheetId);
        }
    }, [selectedTimeSheetId, getRoleByEmpIdOptions]);

    useEffect(() => {
        if (selectedTimeSheetId && selectedRoleId) {
            getTaskByEmpIdOptions(selectedTimeSheetId, selectedRoleId);
        }
    }, [selectedTimeSheetId, getRoleByEmpIdOptions, selectedRoleId, getTaskByEmpIdOptions]);

    const approveTimesheet = useCallback(
        async (record) => {
            try {
                dispatch(setIsLoading(true));
                const requestBody = {
                    companyId: currentCompany,
                    actionId: currentEmployee,
                    employeeId: currentEmployee,
                    myTimeSheetListId: record.map((r) => r.txnMyTimeSheetId),
                };
                await service.approveMyTimesheet(requestBody);
            } catch (error) {
                message.error(t("common.error"))
            } finally {
                dispatch(setIsLoading(false));
                message.success(t("common.success"))
                getMyTimesheetList({ page: 0, size: 10 });
                getTimesheetCalendarData(startDate, endDate);
            }
        },
        [dispatch, currentCompany, currentEmployee, getMyTimesheetList, getTimesheetCalendarData]
    );

    const onApproveTimesheet = useCallback(
        (selectedRecords) => {
            return new Promise((resolve) => {
                const count = selectedRecords.length;

                Modal.confirm({
                    title: t("my_timesheet.label.confirm_send_approve"),
                    content: (
                        <span>
                            {i18n.language == "en" ? `You are about to submit  ${count} requests. Please check the correctness before proceeding`
                                : `คุณกำลังจะส่งคำขอทั้งหมด ${count} รายการโปรดตรวจสอบความถูกต้องก่อนดำเนินการ`}
                        </span>
                    ),
                    onOk() {
                        return approveTimesheet(selectedRecords).then(resolve);
                    },
                    okText: t('common.confirm'),
                    cancelText: t('common.cancel'),
                    okButtonProps: { style: { height: "40px", borderRadius: "8px", backgroundColor: "var(--button-color)", boxShadow: "none" } },
                    cancelButtonProps: { style: { height: "40px", borderRadius: "8px", boxShadow: "none" } },
                });
            });
        },
        [approveTimesheet, i18n.language, t]
    );

    const handleSearch = (values) => {
        setSearch({})

        try {
            setSearch({
                ...values,
            })
            getMyTimesheetList({
                ...values,
                month: values?.month?.format("MM"),
                startDate: values?.dateTimeRecord?.format("YYYY-MM-DD"),
                page: 0, size: 10
            })
        } catch (error) { }
    };

    const deleteMyTimesheet = useCallback(async (params) => {
        dispatch(setIsLoading(true))
        try {
            const response = await service.deleteMyTimesheet({
                companyId: currentCompany,
                employeeId: currentEmployee,
                txnMyTimeSheetId: params?.txnMyTimeSheetId
            })
        } catch (error) {
            message.error(t("common.error"))
        } finally {
            getMyTimesheetList({ page: 0, size: 10 })
            dispatch(setIsLoading(false))
            message.success(t("common.success"))
        }
    }, [dispatch, currentCompany, currentEmployee, getMyTimesheetList])

    const onDeleteMyTimesheet = (e) => {
        Modal.confirm({
            title: t('dialog.confirmation.header'),
            content: (
                <>
                    <p>{t('dialog.confirmation.delete')}</p>
                </>
            ),
            onOk() {
                deleteMyTimesheet(e)
            },
            okText: t('confirm-2'),
            cancelText: t('masterData.cancel'),
        });
    };

    return {
        isLoading,
        activePanel,
        setActivePanel,
        onUpdateTimesheet,
        pagination,
        myTimesheetList,
        onPageChange,
        setEditRecord,
        editRecord,
        timesheetOptions: modifiedTimesheetOption,
        roleOptions: modifiedRoleOption,
        taskOptions: modifiedTaskOption,
        setSelectedTimeSheetId,
        setSelectedRoleId,
        onApproveTimesheet,
        handleSearch,
        onDeleteMyTimesheet
    }
}

export default useUpdateMyTimesheet;